import { esc, icon } from './format.ts';
import type { Me } from './session.ts';

/**
 * The platform's notice to every agency (#284, ADR-0028): a band above the screen
 * (`#platform-notice`, outside `#app`, which every render rewrites) for the agencies' people,
 * never for Nubi's own organization. Plain text, painted escaped. Each person closes it for their
 * session (this tab: sessionStorage, a reload keeps it closed); the API gives every new text or
 * tone a new id, and a new id shows again. Signing out forgets what was closed, so the next
 * person at a shared counter sees it.
 */

export type NoticeTone = 'INFO' | 'IMPORTANT';
export type PlatformNotice = { id: string; text: string; tone: NoticeTone; endsOn: string | null };

/** Each tone in the person's words (never by color alone) and its icon. */
export const NOTICE_TONE: Record<NoticeTone, { label: string; icon: string; cls: string }> = {
  INFO: { label: 'Información', icon: 'bell', cls: 'info' },
  IMPORTANT: { label: 'Importante', icon: 'warn', cls: 'important' },
};

const CLOSED_KEY = 'nubi.notice.closed';
/** What this page closed when the browser refuses storage (a private window). */
let closedHere: string | null = null;

const closedId = () => {
  try {
    return sessionStorage.getItem(CLOSED_KEY) ?? closedHere;
  } catch {
    return closedHere;
  }
};
const rememberClosed = (id: string) => {
  closedHere = id;
  try {
    sessionStorage.setItem(CLOSED_KEY, id);
  } catch {
    // Storage refused: closed for this page only.
  }
};
/** The session ended: the next person sees the notice again. */
export function forgetClosedNotice() {
  closedHere = null;
  try {
    sessionStorage.removeItem(CLOSED_KEY);
  } catch {
    // Nothing was stored.
  }
}

/**
 * The band's content: the tone in words, the text, and «Cerrar aviso» when it can be closed (the
 * administration's preview cannot). An «importante» notice is announced once, when painted.
 */
export function noticeHtml(n: Pick<PlatformNotice, 'text' | 'tone'>, closable = true) {
  const t = NOTICE_TONE[n.tone] ?? NOTICE_TONE.INFO;
  return `<div class="platform-notice-inner ${t.cls}"${n.tone === 'IMPORTANT' ? ' role="alert"' : ''}>
    ${icon(t.icon)}<p class="platform-notice-text"><strong class="platform-notice-tone">${esc(t.label)}</strong> <span>${esc(n.text)}</span></p>
    ${closable ? `<button type="button" class="platform-notice-close" data-notice-close aria-label="Cerrar aviso">${icon('close')}</button>` : ''}
  </div>`;
}

/** No band: nobody signed in, or the session ended. */
export function hideNotice(band: HTMLElement) {
  band.hidden = true;
  band.innerHTML = '';
  delete band.dataset.notice;
}

/** Paints the notice in force for an agency person who has not closed it; otherwise no band. */
export function paintNotice(band: HTMLElement, me: Me | null) {
  const n = me?.organizationType === 'AGENCY' ? me.notice : null;
  if (!n || closedId() === n.id) return hideNotice(band);
  band.innerHTML = noticeHtml(n);
  band.dataset.notice = n.id;
  band.hidden = false;
  if (band.dataset.wired) return;
  band.dataset.wired = 'true';
  band.addEventListener('click', (ev) => {
    if (!(ev.target as HTMLElement).closest('[data-notice-close]')) return;
    if (band.dataset.notice) rememberClosed(band.dataset.notice);
    hideNotice(band);
    // The focus was on the button that is gone: back to the screen, never lost.
    document.querySelector<HTMLElement>('#main')?.focus({ preventScroll: true });
  });
}
